<script setup lang="ts">
import { computed, defineProps } from 'vue';
import { storeToRefs } from 'pinia';
import moment from 'moment';
import InfoIcon from '@/dashboard/vue/components/icons/InfoIcon.vue';
import { usePurchaseStore } from '@/dashboard/store/purchaseStore';
import { Workspace, WorkspaceUser } from '@/dashboard/types/workspace';
import Button from '@/dashboard/vue/components/common/Button.vue';
import { formatBytes } from '@/dashboard/utils/formatBytes';
import { UserRole, UserRoleType } from '@/dashboard/types/user_role';
import Divider from '@/dashboard/vue/components/common/Divider.vue';
import LoaderIcon from '@/dashboard/vue/components/icons/LoaderIcon.vue';
import Tooltip from '@/dashboard/vue/components/common/Tooltip.vue';

const props = defineProps<{
  workspace: Workspace;
  workspaceUser: WorkspaceUser;
}>();

const purchaseStore = usePurchaseStore();

const { loading } = storeToRefs(purchaseStore);

const bytesUsed = computed(() => formatBytes(props.workspace.usage.used_total));
const bytesTotal = computed(() => formatBytes(props.workspace.usage.total));
const bytesUsedPercent = computed(() => Math.min(100, (props.workspace.usage.used_total * 100) / props.workspace.usage.total));
const limitError = computed(() => props.workspace.usage.total < props.workspace.usage.used_total);
const hasSubscription = computed(() => !!props.workspace.subscription);
const canBuySubscription = computed(() => {
  const roles: UserRoleType[] = [UserRole.Owner];
  return roles.includes(props.workspaceUser.role);
});
const cancelDate = computed(() => {
  if (props.workspace.subscription?.ends_at) {
    const date = moment(props.workspace.subscription.ends_at);
    return date.format('DD.MM.YYYY');
  }

  return '';
});

function openPurchasePopup() {
  purchaseStore.openPopup(props.workspace, props.workspaceUser);
}
</script>

<template>
  <div class="mb-[28px] flex flex-col items-center laptop:items-start">
    <div class="flex flex-col items-center gap-y-[15px] laptop:flex-row laptop:items-start">
      <div class="relative">
        <span
          class="font-feature-slashed-zero inline-flex text-[48px] font-bold leading-[80px] small-desktop:text-[80px]"
          :class="{ 'text-hard-error': limitError, 'text-brand-night-blue': !limitError }"
        >
          <span>{{ bytesUsed }}</span>
        </span>
        <div class="text-caption-s mt-[9px] flex justify-center laptop:mt-zero">
          <span class="flex items-center justify-center gap-x-xs">
            <span class="text-black dark:text-gray-500">Used</span>
            <Tooltip>
              <template #title>
                <InfoIcon class="cursor-pointer stroke-gray-400 dark:stroke-gray-650" />
              </template>
              <template #body>
                <span class="w-[264px]">The volume of storage used by all users (types Owner, Admin, and Creator)</span>
              </template>
            </Tooltip>
          </span>
        </div>
      </div>

      <span class="mx-s hidden text-[48px] font-bold leading-[80px] text-black dark:text-gray-700 laptop:inline small-desktop:text-[80px]">/</span>

      <div class="relative">
        <span
          class="font-feature-slashed-zero inline-flex text-[48px] font-bold leading-[80px] text-black dark:text-gray-350 small-desktop:text-[80px]"
        >
          <span>{{ bytesTotal }}</span>
        </span>
        <div class="text-caption-s mt-[9px] flex justify-center laptop:mt-zero">
          <span class="flex items-center justify-center gap-x-xs">
            <span class="text-black dark:text-gray-500">Total</span>
            <Tooltip>
              <template #title>
                <InfoIcon class="cursor-pointer stroke-gray-400 dark:stroke-gray-650" />
              </template>
              <template #body>
                <span class="w-[264px]">
                  The total amount of storage for all users. Each additional paying user adds storage volume according to your plan.
                </span>
              </template>
            </Tooltip>
          </span>
        </div>
      </div>

      <Button
        v-if="canBuySubscription"
        class="button-colored mt-xs !hidden h-[64px] w-[232px] !rounded-[70px] laptop:ml-l laptop:!flex"
        @click="() => openPurchasePopup()"
      >
        <span v-if="hasSubscription" class="button__text">Change plan</span>
        <span v-else class="button__text">Get more storage</span>
      </Button>
    </div>
  </div>

  <div class="relative mb-[54px] h-[4px] laptop:w-[320px]">
    <div class="absolute h-full w-full rounded-4 bg-gray-250 dark:bg-gray-800"></div>
    <div
      class="absolute h-full rounded-4"
      :class="{ 'bg-brand-night-blue': !limitError, 'bg-hard-error': limitError }"
      :style="{ width: bytesUsedPercent + '%' }"
    >
      <span class="text-caption-s absolute left-[0px] top-[7px] text-black dark:text-gray-500">{{ bytesUsedPercent.toFixed(0) }}%</span>
    </div>
  </div>

  <div v-if="canBuySubscription && cancelDate != ''" class="flex flex-col">
    <Divider class="divider-red" />

    <div class="text-caption-s flex flex-col items-center justify-between gap-y-xs pb-s pt-[13px] text-red laptop:flex-row">
      <span>Downgrading... It keeps the current plan until the paid period ends ({{ cancelDate }})</span>

      <div>
        <Button :disabled="loading" :on-click="purchaseStore.activateSubscription" class="button-colored h-[32px] min-w-[200px]">
          <LoaderIcon v-if="loading" class="animate-spin stroke-white" />
          <span v-else class="button__text text-buttons">Cancel downgrading</span>
        </Button>
      </div>
    </div>
  </div>
</template>
